import crypto from 'crypto'
import zlib from 'zlib'
import fs from 'fs/promises'
import { Defaults, ErrorCode } from '@remotekit/shared'
import { RemoteKitError } from '@remotekit/shared'
import { validatePath } from './security'

export interface TransferSession {
  transferId: string
  path: string
  size: number
  checksum: string
  receivedBytes: number
  chunks: Buffer[]
  createdAt: number
}

export class TransferManager {
  private sessions = new Map<string, TransferSession>()
  private activeCount = 0
  private cleanupInterval: ReturnType<typeof setInterval>
  private allowedPaths: string[]
  private fileBlacklist: string[]
  private maxUploadSize: number

  constructor(options: {
    allowedPaths: string[]
    fileBlacklist: string[]
    maxUploadSize: number
  }) {
    this.allowedPaths = options.allowedPaths
    this.fileBlacklist = options.fileBlacklist
    this.maxUploadSize = options.maxUploadSize
    this.cleanupInterval = setInterval(() => this.cleanupExpired(), 60000)
  }

  /** 开始接收文件（上传：服务端→客户端） */
  async startUpload(params: { path: string; size: number; checksum: string }): Promise<{ transferId: string }> {
    validatePath(params.path, this.allowedPaths, this.fileBlacklist)

    if (params.size > this.maxUploadSize) {
      throw new RemoteKitError(ErrorCode.FILE_TOO_LARGE, `文件大小 ${params.size} 超过限制 ${this.maxUploadSize}`)
    }

    if (this.activeCount >= Defaults.MAX_PARALLEL_TRANSFERS) {
      throw new RemoteKitError(ErrorCode.TRANSFER_FAILED, '并行传输数达到上限')
    }

    const transferId = crypto.randomUUID()
    this.sessions.set(transferId, {
      transferId,
      path: params.path,
      size: params.size,
      checksum: params.checksum,
      receivedBytes: 0,
      chunks: [],
      createdAt: Date.now(),
    })
    this.activeCount++
    return { transferId }
  }

  /** 接收文件分块 */
  async receiveChunk(params: { transferId: string; offset: number; data: Buffer }): Promise<{ received: number }> {
    const session = this.sessions.get(params.transferId)
    if (!session) {
      throw new RemoteKitError(ErrorCode.TRANSFER_EXPIRED, '传输会话不存在或已过期')
    }

    // 解压 gzip
    let chunk: Buffer
    try {
      chunk = zlib.gunzipSync(params.data)
    } catch {
      chunk = params.data
    }

    session.chunks.push(chunk)
    session.receivedBytes += chunk.length
    return { received: session.receivedBytes }
  }

  /** 完成接收，校验并写入文件 */
  async finishUpload(params: { transferId: string }): Promise<{ success: boolean; checksum: string }> {
    const session = this.sessions.get(params.transferId)
    if (!session) {
      throw new RemoteKitError(ErrorCode.TRANSFER_EXPIRED, '传输会话不存在或已过期')
    }

    const data = Buffer.concat(session.chunks)
    const actualChecksum = crypto.createHash('sha256').update(data).digest('hex')

    if (actualChecksum !== session.checksum) {
      this.removeSession(params.transferId)
      throw new RemoteKitError(ErrorCode.TRANSFER_FAILED, '文件校验失败')
    }

    await fs.writeFile(session.path, data)
    this.removeSession(params.transferId)
    return { success: true, checksum: actualChecksum }
  }

  /** 开始发送文件（下载：客户端→服务端） */
  async startDownload(params: { path: string }): Promise<{ transferId: string; size: number; checksum: string }> {
    validatePath(params.path, this.allowedPaths, this.fileBlacklist)

    const data = await fs.readFile(params.path).catch(() => {
      throw new RemoteKitError(ErrorCode.FILE_NOT_FOUND, `文件不存在: ${params.path}`)
    })

    const checksum = crypto.createHash('sha256').update(data).digest('hex')
    const transferId = crypto.randomUUID()

    this.sessions.set(transferId, {
      transferId,
      path: params.path,
      size: data.length,
      checksum,
      receivedBytes: 0,
      chunks: [data],
      createdAt: Date.now(),
    })
    this.activeCount++

    return { transferId, size: data.length, checksum }
  }

  /** 发送文件分块 */
  async sendChunk(params: { transferId: string; offset: number; size: number }): Promise<{ data: Buffer }> {
    const session = this.sessions.get(params.transferId)
    if (!session) {
      throw new RemoteKitError(ErrorCode.TRANSFER_EXPIRED, '传输会话不存在或已过期')
    }

    const fileData = session.chunks[0]
    const chunk = fileData.subarray(params.offset, params.offset + params.size)
    const compressed = zlib.gzipSync(chunk)
    return { data: compressed }
  }

  /** 完成发送 */
  async finishDownload(params: { transferId: string }): Promise<{ success: boolean }> {
    const session = this.sessions.get(params.transferId)
    if (!session) {
      throw new RemoteKitError(ErrorCode.TRANSFER_EXPIRED, '传输会话不存在或已过期')
    }
    this.removeSession(params.transferId)
    return { success: true }
  }

  /** 获取已接收字节数（用于断点续传） */
  getReceivedOffset(transferId: string): number {
    return this.sessions.get(transferId)?.receivedBytes ?? 0
  }

  private removeSession(transferId: string): void {
    if (this.sessions.delete(transferId)) {
      this.activeCount--
    }
  }

  private cleanupExpired(): void {
    const now = Date.now()
    for (const [id, session] of this.sessions) {
      if (now - session.createdAt > Defaults.TRANSFER_SESSION_TIMEOUT) {
        this.removeSession(id)
      }
    }
  }

  destroy(): void {
    clearInterval(this.cleanupInterval)
    this.sessions.clear()
    this.activeCount = 0
  }
}
